import Loader from '@/components/ui/loader';
import { statusIconVariants } from '@/components/ui/session-status/status.variants';
import { useVisibleSessionStatuses } from '@/components/ui/session-status/use-visible-session-statuses';
import { consolidatedSessionsListMessages } from '@/features/consolidatedSessionsList/consolidatedSessionsList.messages';
import { cn } from '@/lib/cn';
import { Checkbox } from '@heidi/design-system/checkbox';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { type SessionStatus } from '@heidi/schema/ml-scribe';
import { statusNameMessages } from '@heidi/session';
import { useQuerySessionCountBySessionStatus } from '@heidi/session/data';
import { FormattedMessage, useIntl } from 'react-intl';

type StatusFilterPanelProps = {
  selectedStatusesFilter: SessionStatus[];
  handleStatusChange: (status: SessionStatus) => void;
};

type StatusItemProps = {
  status: SessionStatus;
  selectedStatusesFilter: SessionStatus[];
  handleStatusChange: (status: SessionStatus) => void;
};

const StatusItem = ({
  status,
  selectedStatusesFilter,
  handleStatusChange,
}: StatusItemProps) => {
  const intl = useIntl();
  const { data: { count: sessionCount } = { count: 0 }, isLoading } =
    useQuerySessionCountBySessionStatus(status);

  return (
    <button
      type="button"
      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-background-secondary"
      onClick={() => handleStatusChange(status)}
    >
      <Checkbox checked={selectedStatusesFilter.includes(status)} />
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'size-2 shrink-0 rounded-full',
              statusIconVariants({
                variant: status.toLowerCase() as keyof typeof statusIconVariants,
              })
            )}
          />
          <TypographyP2 className="truncate">
            {intl.formatMessage(statusNameMessages[status])}
          </TypographyP2>
        </div>
        {isLoading ? (
          <Loader size={14} />
        ) : (
          sessionCount > 0 && (
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage
                {...consolidatedSessionsListMessages.resultCount}
                values={{ count: sessionCount }}
              />
            </TypographyCaption>
          )
        )}
      </div>
    </button>
  );
};

const StatusFilterPanel = ({
  selectedStatusesFilter,
  handleStatusChange,
}: StatusFilterPanelProps) => {
  const sessionStatusOrder = useVisibleSessionStatuses();
  return (
    <div className="flex max-h-[360px] flex-col gap-0.5 overflow-y-auto">
      {sessionStatusOrder.map((status) => (
        <StatusItem
          key={status}
          status={status}
          selectedStatusesFilter={selectedStatusesFilter}
          handleStatusChange={handleStatusChange}
        />
      ))}
    </div>
  );
};

export default StatusFilterPanel;
